import { supabase } from './client';

/**
 * Playlist CRUD operations for registered users.
 * All operations require authenticated Supabase session.
 * RLS policies enforce user-level access control.
 *
 * @author Claude + Kip McClanahan (2026-03-21)
 */

export type Playlist = {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  health_goal_id: string | null;
  cover_color: string | null;
  created_at: string;
  updated_at: string;
};

export type PlaylistItem = {
  id: string;
  playlist_id: string;
  article_id: string;
  position: number;
  added_at: string;
};

export type PlaybackProgress = {
  id: string;
  user_id: string;
  article_id: string;
  position_seconds: number;
  completed: boolean;
  playback_speed: number;
  updated_at: string;
};

// ---- external_id → uuid resolution ----

/**
 * The audio tables key `article_id` as `uuid REFERENCES public_articles(id)`
 * (see supabase migration `20260321000000_audio_playlists.sql`). But the app
 * carries the app-land `external_id` (TEXT, e.g. `3bb6ac5f07d1`) everywhere as
 * `article.id` / `QueueTrack.id` — `mapRow` aliases `id: row.external_id ?? row.id`
 * (`lib/supabase/mappers.ts:190`). Feeding that TEXT id straight into the uuid
 * column 400s with `22P02 invalid input syntax for type uuid`.
 *
 * INVARIANT: every audio-table query that filters or writes `article_id` MUST
 * resolve the external_id to the real uuid first. We do that via a public read
 * on `public_articles` (works for anon + authed; published rows are public-read),
 * cached per session so the 10s playback-progress saves don't re-query.
 * See the external_id↔uuid rule in the Research Library SSOT.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const articleUuidCache = new Map<string, string>();

async function resolveArticleUuid(articleExternalId: string): Promise<string | null> {
  if (!articleExternalId) return null;
  // Defensive: a caller may already hold the real uuid PK — pass it through.
  if (UUID_RE.test(articleExternalId)) return articleExternalId;

  const cached = articleUuidCache.get(articleExternalId);
  if (cached) return cached;
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('public_articles')
    .select('id')
    .eq('external_id', articleExternalId)
    .maybeSingle();
  if (error) { console.error('resolveArticleUuid:', error); return null; }

  const uuid = (data?.id as string) ?? null;
  if (uuid) articleUuidCache.set(articleExternalId, uuid);
  return uuid;
}

// ---- Playlists ----

export async function fetchUserPlaylists(): Promise<Playlist[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('audio_playlists')
    .select('*')
    .order('updated_at', { ascending: false });
  if (error) { console.error('fetchUserPlaylists:', error); return []; }
  return data || [];
}

export async function createPlaylist(
  name: string,
  description?: string,
  healthGoalId?: string,
  coverColor?: string,
): Promise<Playlist | null> {
  if (!supabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('audio_playlists')
    .insert({
      user_id: user.id,
      name,
      description: description || null,
      health_goal_id: healthGoalId || null,
      cover_color: coverColor || null,
    })
    .select()
    .single();

  if (error) { console.error('createPlaylist:', error); return null; }
  return data;
}

export async function deletePlaylist(playlistId: string): Promise<boolean> {
  if (!supabase) return false;
  const { error } = await supabase
    .from('audio_playlists')
    .delete()
    .eq('id', playlistId);
  if (error) { console.error('deletePlaylist:', error); return false; }
  return true;
}

export async function renamePlaylist(
  playlistId: string,
  name: string,
): Promise<boolean> {
  if (!supabase) return false;
  const { error } = await supabase
    .from('audio_playlists')
    .update({ name, updated_at: new Date().toISOString() })
    .eq('id', playlistId);
  if (error) { console.error('renamePlaylist:', error); return false; }
  return true;
}

// ---- Playlist Items ----

export async function fetchPlaylistItems(
  playlistId: string,
): Promise<PlaylistItem[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('audio_playlist_items')
    .select('*')
    .eq('playlist_id', playlistId)
    .order('position', { ascending: true });
  if (error) { console.error('fetchPlaylistItems:', error); return []; }
  return data || [];
}

export async function addToPlaylist(
  playlistId: string,
  articleExternalId: string,
  position?: number,
): Promise<boolean> {
  if (!supabase) return false;
  const articleId = await resolveArticleUuid(articleExternalId);
  if (!articleId) { console.error('addToPlaylist: unresolved article', articleExternalId); return false; }
  const { error } = await supabase
    .from('audio_playlist_items')
    .upsert(
      { playlist_id: playlistId, article_id: articleId, position: position ?? 0 },
      { onConflict: 'playlist_id,article_id' },
    );
  if (error) { console.error('addToPlaylist:', error); return false; }
  // Update playlist timestamp
  await supabase
    .from('audio_playlists')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', playlistId);
  return true;
}

export async function removeFromPlaylist(
  playlistId: string,
  articleExternalId: string,
): Promise<boolean> {
  if (!supabase) return false;
  const articleId = await resolveArticleUuid(articleExternalId);
  if (!articleId) { console.error('removeFromPlaylist: unresolved article', articleExternalId); return false; }
  const { error } = await supabase
    .from('audio_playlist_items')
    .delete()
    .eq('playlist_id', playlistId)
    .eq('article_id', articleId);
  if (error) { console.error('removeFromPlaylist:', error); return false; }
  return true;
}

// ---- Playback Progress ----

export async function getPlaybackProgress(
  articleExternalId: string,
): Promise<PlaybackProgress | null> {
  if (!supabase) return null;
  const articleId = await resolveArticleUuid(articleExternalId);
  if (!articleId) return null;
  const { data, error } = await supabase
    .from('audio_playback_progress')
    .select('*')
    .eq('article_id', articleId)
    .single();
  if (error && error.code !== 'PGRST116') { // PGRST116 = not found
    console.error('getPlaybackProgress:', error);
  }
  return data || null;
}

export async function savePlaybackProgress(
  articleExternalId: string,
  positionSeconds: number,
  completed: boolean,
  playbackSpeed: number,
): Promise<boolean> {
  if (!supabase) return false;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const articleId = await resolveArticleUuid(articleExternalId);
  if (!articleId) { console.error('savePlaybackProgress: unresolved article', articleExternalId); return false; }

  const { error } = await supabase
    .from('audio_playback_progress')
    .upsert(
      {
        user_id: user.id,
        article_id: articleId,
        position_seconds: positionSeconds,
        completed,
        playback_speed: playbackSpeed,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,article_id' },
    );
  if (error) { console.error('savePlaybackProgress:', error); return false; }
  return true;
}

export async function getAllPlaybackProgress(): Promise<PlaybackProgress[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('audio_playback_progress')
    .select('*')
    .order('updated_at', { ascending: false });
  if (error) { console.error('getAllPlaybackProgress:', error); return []; }
  return data || [];
}
